import { JSX, useEffect, useState } from 'react';
import moment from 'moment';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Department,
  GetAllDepartmentResponse,
  GetAllIncidentTypesResponse,
  GetIncidentByIdResponse,
  IncidentTypes,
  LoginResponseData,
} from '@EHSNavigator/backend';
import { getIncidentReportingSchema } from './schema';
import { formatCurrentDate } from '../../utils/dateFormat';
import { useApiClients } from '../../client/api/useApiClient';
import IncidentReportingStepOne from '../../views/IncidentReportingStepOne';
import IncidentReportingStepTwo from '../../views/IncidentReportingStepTwo';
import IncidentReportingStepThree from '../../views/IncidentReportingStepThree';
import IncidentReportingStepFour from '../../views/IncidentReportingStepFour';
import { useNavigate, useSearchParams } from 'react-router';
import { useLocalStorage } from '@uidotdev/usehooks';
import Loader from '../../components/Loader';
import NoDepartment from '../../components/NoDepartment';
import { useDispatch } from 'react-redux';
import { toggleModal } from '../../reducers/modalToggle';
import { showBodyScroll } from '../../utils/bodyScroll';
import { hideBodyScroll } from '../../utils/bodyScroll';
import { useThrottleAsyncFn } from '../../utils/useThrottleAsyncFn';

export function convertTo12HourFormat(time24: string) {
  if (typeof time24 !== 'string' || !time24.includes(':')) {
    return null;
  }
  const [hourStr, minute] = time24.split(':');
  let hour = parseInt(hourStr, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';

  hour = hour % 12 || 12;

  const hourFormatted = hour.toString().padStart(2, '0');

  return `${hourFormatted}:${minute} ${ampm}`;
}

export default function IncidentReportingPage() {
  const [user] = useLocalStorage<LoginResponseData | null>('userInfo', null);
  const { IncidentReportingApi, DepartmentApi } = useApiClients();
  const dispatch = useDispatch();
  const [attachments, setAttachments] = useState<
    GetIncidentByIdResponse['incident']['attachments']
  >([]);
  const {
    data: allDepartments,
    isLoading: departmentsLoading,
    isRefetching,
  } = useQuery<GetAllDepartmentResponse>({
    queryKey: ['incident-reporting-departments'],
    queryFn: async () => {
      const response =
        await DepartmentApi.departmentControllerFindAllDepartment({
          disablePagination: true,
        });
      return response?.data;
    },
  });

  const { data: incidentTypesResponse, isLoading: incidentsLoading } =
    useQuery<GetAllIncidentTypesResponse>({
      queryKey: ['incident-typess'],
      queryFn: async () => {
        const response =
          await IncidentReportingApi.incidentReportingControllerFindAllIncidentTypes();
        return response?.data;
      },
    });

  const departments =
    allDepartments?.departments?.map((node: Department) => ({
      label: node?.name,
      value: node?.id,
    })) || [];

  const incidentTypes =
    incidentTypesResponse?.incidentTypes?.map((node: IncidentTypes) => ({
      label: node?.name,
      value: node?.id,
    })) || [];

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('draft-id') ?? '';

  const navigate = useNavigate();

  const reportIncident = useMutation({
    mutationFn: async (payload: any) => {
      const response =
        await IncidentReportingApi.incidentReportingControllerCreate(payload);
      return response;
    },
    onSuccess: (data: any, variables: any) => {
      toast.success(data?.data?.message);
      if (!variables?.isDraft) {
        handleNextStep(4);
        dispatch(toggleModal(''));
      } else {
        navigate(`/report-incident/drafts/`);
      }
    },
    onError: (error: any) => {
      dispatch(toggleModal(''));
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });

  const reportIncidentUpdate = useMutation({
    mutationFn: async (payload: any) => {
      return await IncidentReportingApi.incidentReportingControllerUpdateDraft(
        payload,
      );
    },
    onSuccess: (data: any, variables: any) => {
      toast.success(data?.data?.message);
      if (!variables?.isDraft) {
        handleNextStep(4);
        dispatch(toggleModal(''));
      } else {
        navigate(`/report-incident/drafts/`);
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });

  const { data: drafted, isLoading: isDraftLoading } = useQuery<
    GetIncidentByIdResponse['incident']
  >({
    queryKey: ['get-draft-' + draftId],
    queryFn: async () => {
      const response =
        await IncidentReportingApi.incidentReportingControllerFindIncidentById({
          id: draftId,
        });
      return response?.data?.incident;
    },
    enabled: !!draftId,
    refetchOnWindowFocus: false,
  });

  const initialValues = {
    employeeId: user?.user?.employeeId ?? '---',
    name: user?.user?.fullName || '',
    role: user?.user?.role?.name || '',
    title: '',
    dateOfReport: formatCurrentDate(),
    date: new Date(),
    time: moment().format('HH:mm'),
    dateTimeUndetermined: false,
    factory: '',
    department: '',
    contractorInvolved: '',
    incidentType: '',
    incidentSeverity: '',
    multiplePeopleInjured: false,
    affectedBodyParts: [],
    description: '',
    files: [],
  };

  const incidentReportingHandler = useFormik({
    enableReinitialize: false,
    initialValues: initialValues,
    validationSchema: getIncidentReportingSchema(
      currentStep,
      attachments,
      incidentTypes,
    ),
    onSubmit: async (values) => {
      try {
        if (currentStep === 3) {
          const payload = {
            isDraft: false,
            title: values?.title,
            date: values?.dateTimeUndetermined
              ? null
              : values?.date
                ? moment(values?.date).format('DD/MM/YYYY')
                : null,
            time: values?.dateTimeUndetermined
              ? null
              : moment(values?.time, 'HH:mm A').format('hh:mmA') ===
                  'Invalid date'
                ? null
                : moment(values?.time, 'HH:mm A').format('hh:mmA'),
            dateUndetermined: values?.dateTimeUndetermined,
            location: values?.factory,
            departmentId: values?.department,
            contractorInvolved: values?.contractorInvolved,
            incidentType: values?.incidentType,
            severity: values?.incidentSeverity,
            wereManyInjured: values?.multiplePeopleInjured,
            description: values?.description,
            files: values?.files,
            affectedBodyParts: values?.affectedBodyParts,
          };
          if (draftId) {
            await reportIncidentUpdate.mutateAsync({
              ...payload,
              incidentId: draftId,
              attachmentIds: attachments?.map((node) => node?.id) || [],
            });
          } else {
            await reportIncident.mutateAsync(payload);
          }
        }
        incidentReportingHandler?.resetForm();
        setAttachments([]);
        setCurrentStep(4);
        navigate('/report-incident/report');
      } catch (error: any) {
        console.error(error);
      }
    },
  });

  useEffect(() => {
    if (drafted) {
      incidentReportingHandler.setValues((prev: any) => ({
        ...prev,
        employeeId: user?.user?.employeeId ?? '---',
        name: user?.user?.fullName || '',
        role: user?.user?.role?.name || '',
        title: drafted?.title || '',
        dateOfReport: formatCurrentDate(),
        date: drafted?.incidentDate || '',
        time: drafted?.incidentTime || '',
        dateTimeUndetermined: drafted?.dateUndetermined ?? false,
        factory: drafted?.location || '',
        department: drafted?.department?.id || '',
        contractorInvolved: drafted?.wereContractorInvolved || '',
        incidentType: drafted?.incidentTypeId || '',
        incidentSeverity: drafted?.severity || '',
        multiplePeopleInjured: drafted?.wereManyInjured ?? false,
        description: drafted?.description || '',
        files: [],
        affectedBodyParts: drafted?.affectedBodyParts || [],
      }));
      if ((drafted?.attachments || [])?.length > 0) {
        setAttachments(drafted?.attachments);
      } else {
        setAttachments([]);
      }
    }
  }, [drafted]);

  const handleNextStep = async (steps: number) => {
    if (steps < currentStep) {
      setCurrentStep(steps);
      window.scrollTo(0, 0);
    } else {
      const errors = await incidentReportingHandler.validateForm();
      if (Object.keys(errors).length === 0) {
        window.scrollTo(0, 0);
        setCurrentStep(steps);
      } else {
        const touchedFields = Object.keys(errors).reduce((acc: any, key) => {
          acc[key] = true;
          return acc;
        }, {});

        incidentReportingHandler.setTouched(touchedFields);
      }
    }
  };

  async function handleSaveAsDraft() {
    const { values, setFieldError, setTouched } = incidentReportingHandler;

    if (values.title?.trim() === '') {
      setFieldError('title', 'Title is required');
      setTouched({ title: true }, true);
      return;
    }
    const cleanedData = {
      isDraft: true,
      title: values?.title,
      date: values?.dateTimeUndetermined
        ? null
        : values?.date
          ? moment(values?.date).format('DD/MM/YYYY')
          : null,
      time: values?.dateTimeUndetermined
        ? null
        : moment(values?.time, 'HH:mm A').format('hh:mmA') === 'Invalid date'
          ? null
          : moment(values?.time, 'HH:mm A').format('hh:mmA'),
      dateUndetermined: values?.dateTimeUndetermined,
      location: values?.factory ? values?.factory : null,
      departmentId: values?.department,
      contractorInvolved: values?.contractorInvolved,
      incidentType: values?.incidentType,
      severity: values?.incidentSeverity,
      wereManyInjured: values?.multiplePeopleInjured,
      description: values?.description,
      files: values?.files,
      affectedBodyParts: values?.affectedBodyParts,
    };

    const removeEmptyValues = Object.fromEntries(
      Object.entries(cleanedData).filter(([, value]) => {
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'boolean') return value;
        return value !== '' && value !== null && value !== undefined;
      }),
    );

    if (draftId) {
      await reportIncidentUpdate.mutateAsync({
        ...removeEmptyValues,
        incidentId: drafted?.id,
        attachmentIds: attachments?.map((node) => node?.id) || [],
      });
    } else {
      await reportIncident.mutateAsync(removeEmptyValues);
    }
  }
  const throttledSaveAsDraft = useThrottleAsyncFn(handleSaveAsDraft, 3000);

  const submittedIncidentResponse =
    reportIncident?.data?.data?.incident ||
    reportIncidentUpdate?.data?.data?.incident;

  function areObjectsEqual(obj1: any, obj2: any) {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length !== keys2.length) return false;
    return keys1.every((key) => obj1[key] === obj2[key]);
  }

  const isFormUnchanged = areObjectsEqual(
    {
      title: incidentReportingHandler.values?.title,
      date: moment(incidentReportingHandler.values?.date).format('DD/MM/YYYY'),
      time: incidentReportingHandler.values?.time,
      dateTimeUndetermined:
        incidentReportingHandler.values?.dateTimeUndetermined,
      factory: incidentReportingHandler.values?.factory,
      department: incidentReportingHandler.values?.department,
      contractorInvolved: incidentReportingHandler.values?.contractorInvolved,
      incidentType: incidentReportingHandler.values?.incidentType,
      incidentSeverity: incidentReportingHandler.values?.incidentSeverity,
      multiplePeopleInjured:
        incidentReportingHandler.values?.multiplePeopleInjured,
      description: incidentReportingHandler.values?.description,
      files: JSON.stringify(incidentReportingHandler?.values?.files),
      attachments: JSON.stringify(attachments),
      affectedBodyParts: JSON.stringify(
        incidentReportingHandler.values?.affectedBodyParts,
      ),
    },
    {
      title: drafted?.title || '',
      date:
        moment(drafted?.incidentDate).format('DD/MM/YYYY') ||
        moment(new Date()).format('DD/MM/YYYY'),
      time: drafted?.incidentTime || '',
      dateTimeUndetermined: drafted?.dateUndetermined ?? false,
      factory: drafted?.location || '',
      department: drafted?.department?.id || '',
      contractorInvolved: drafted?.wereContractorInvolved || '',
      incidentType: drafted?.incidentTypeId || '',
      incidentSeverity: drafted?.severity || '',
      multiplePeopleInjured: drafted?.wereManyInjured ?? false,
      description: drafted?.description || '',
      files: JSON.stringify(initialValues?.files || []),
      attachments: JSON.stringify(drafted?.attachments),
      affectedBodyParts: JSON.stringify(drafted?.affectedBodyParts),
    },
  );

  const steps: Record<number, JSX.Element> = {
    1: (
      <IncidentReportingStepOne
        isDraftLoading={
          isFormUnchanged ||
          incidentReportingHandler?.values?.title === '' ||
          reportIncidentUpdate?.isPending ||
          reportIncident?.isPending
        }
        disbaleButtons={
          reportIncidentUpdate?.isPending || reportIncident?.isPending
        }
        formHandler={incidentReportingHandler}
        handleSaveAsDraft={throttledSaveAsDraft}
        handleSteps={handleNextStep}
      />
    ),
    2: (
      <IncidentReportingStepTwo
        formHandler={incidentReportingHandler}
        handleSaveAsDraft={throttledSaveAsDraft}
        handleSteps={handleNextStep}
        attachments={attachments}
        isDraftLoading={
          isFormUnchanged ||
          incidentReportingHandler?.values?.title === '' ||
          reportIncidentUpdate?.isPending ||
          reportIncident?.isPending
        }
        disbaleButtons={
          reportIncidentUpdate?.isPending || reportIncident?.isPending
        }
        handleChangeAttachments={(e) => setAttachments(e)}
        departments={departments}
        incidentTypes={incidentTypes}
      />
    ),
    3: (
      <IncidentReportingStepThree
        formHandler={incidentReportingHandler}
        handleSaveAsDraft={throttledSaveAsDraft}
        handleSteps={handleNextStep}
        isDraftLoading={
          isFormUnchanged ||
          incidentReportingHandler?.values?.title === '' ||
          reportIncidentUpdate?.isPending ||
          reportIncident?.isPending
        }
        disbaleButtons={
          reportIncidentUpdate?.isPending || reportIncident?.isPending
        }
        departments={departments}
        attachments={attachments}
        incidentTypes={incidentTypes}
      />
    ),
    4: (
      <IncidentReportingStepFour
        submittedIncidentResponse={submittedIncidentResponse}
        handleSteps={handleNextStep}
      />
    ),
  };

  useEffect(() => {
    const handleScroll = () => {
      if (departments?.length === 0) {
        hideBodyScroll();
      } else {
        showBodyScroll();
      }
    };

    handleScroll();
    return () => {
      showBodyScroll();
    };
  }, [departments]);

  if (
    isDraftLoading ||
    incidentsLoading ||
    departmentsLoading ||
    isRefetching
  ) {
    return <Loader />;
  }

  return (
    <div className="relative flex w-full flex-1 flex-col px-15 pt-14 pb-10 md:px-25 lg:pr-14 lg:pb-20 lg:pl-8">
      <form
        action="#"
        className="flex flex-1 flex-col"
        onSubmit={incidentReportingHandler?.handleSubmit}
      >
        {steps[currentStep]}
      </form>
      {departments?.length === 0 && <NoDepartment />}
    </div>
  );
}
